import { supabase } from "./supabase";
import type { Session } from "@supabase/supabase-js";
import { startCapture, startPhotoCapture, onPhotoSelected } from "./lib/capture-flow";
import { multiDraftModel, multiDraftFromExtractions, normalizeInstant, type Draft, type Extraction, type ExistingRef, type ItemType, type ModelExtraction, type Route } from "./lib/extract";
import { nextStatus, type ItemAction, type ItemStatus } from "./lib/item-status";
import { localDateStr, type ViewItem } from "./lib/view";
import { accountSheetHTML, announcement, avatarPaletteHTML, captureSheetHTML, confirmationHTML, draftHTML, escapeHtml, formatDateShort, itemSheetHTML, joinGroceryTitle, reviewListHTML, settingsSheetHTML, sheetShell, slippedSheetHTML } from "./lib/render";
import { ingestStats, mailboxState, tallyStatus, type IngestRunRow } from "./lib/ingest-stats";
import { feedbackListModel, validateFeedback, type FeedbackRow } from "./lib/feedback";
import { activeAlerts, alertToExtraction, type AlertItem, type AlertRow } from "./lib/alerts";
import { AVATAR_PALETTE, takenColours } from "./lib/owner";
import { renderShell, digestSheetHTML, type CalMode, type SeeContext, type Tab } from "./lib/render-see";
import { createFocusTrap, type FocusTrap } from "./lib/focus-trap";
import { shouldRerender } from "./lib/session-guard";
import { initTheme, toggleTheme, type ThemeStore } from "./lib/theme";
import {
  applyOverrides,
  buildAccentPrefsUpdate,
  buildThemePrefsUpdate,
  clearOverrides,
  nearestTheme,
  themeOverrides,
  type MemberPrefs,
} from "./lib/member-theme";
import { applyBackground } from "./lib/background";
import { deriveTerms } from "./lib/ics";
import { extractPaletteFromImage } from "./design/palette";
import {
  confirmationModel,
  cursorISO,
  dayModel,
  defaultFilter,
  filterByScope,
  isTodo,
  monthModel,
  openDay,
  openMonth,
  openWeek,
  relativeDayLabel,
  restatementText,
  shiftWindow,
  tasksModel,
  todayModel,
  trolleyModel,
  weekModel,
} from "./lib/planner";
import { completeRecurringTask, recurrenceLabel, rruleForPreset, presetForRrule } from "./lib/recurrence";
import { mapsSearchUrl, mapsDirectionsUrl } from "./lib/maps";
import { digestModel, digestPeriod, type DigestModel } from "./lib/digest";

// Feature 003 — the calm family planner. Two verbs: See (Today ⇄ Week, person-filtered) and
// Capture (a summonable composer). States: signed out → login; signed in + linked → the See
// surface; signed in but unlinked → neutral "not invited" (REQ-AUTH-003).

const app = document.getElementById("app")!;
const sheetRoot = document.getElementById("sheet-root")!; // capture sheet lives here, outside #app
const liveRegion = document.getElementById("live")!; // polite announcements (REQ-UX-002)
const DEFAULT_BG = "/default-bg.jpg"; // the calm default backdrop when a member hasn't set their own

function announce(msg: string) {
  liveRegion.textContent = "";
  liveRegion.textContent = msg; // reset then set so the same message re-announces
}

// Lift any open bottom sheet above the on-screen keyboard (mockup-v9). Without this the phone
// keyboard covers the sheet's inputs (the composer, the item Edit, the Trolley add).
if (window.visualViewport) {
  const vv = window.visualViewport;
  const adj = () => {
    const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    sheetRoot.querySelectorAll<HTMLElement>(".sheet").forEach((s) => (s.style.bottom = `${kb}px`));
  };
  vv.addEventListener("resize", adj);
  vv.addEventListener("scroll", adj);
}

// Disable pinch-to-zoom (a calm, app-like surface). The viewport meta handles standalone PWA; iOS
// Safari ignores user-scalable in-browser, so also block its pinch gesture events. Vertical scroll
// (pan-y) is untouched, so the bottom sheets still scroll.
for (const ev of ["gesturestart", "gesturechange", "gestureend"]) {
  document.addEventListener(ev, (e) => e.preventDefault(), { passive: false });
}

// Register the service worker so the app is installable to the home screen (REQ-PWA-003). The SW is
// network-first for navigations with NO precache of hashed JS/CSS, so a new deploy is never masked
// by a stale bundle. Guarded + best-effort; failure never blocks the app.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

// Apply the persisted / OS-preferred theme before first paint (REQ-DS-002).
const themeStore: ThemeStore = {
  get: () => localStorage.getItem("ho.theme"),
  set: (v) => localStorage.setItem("ho.theme", v),
};
const viewStore = {
  get: () => localStorage.getItem("ho.view"),
  set: (v: string) => localStorage.setItem("ho.view", v),
};
initTheme(
  document.documentElement,
  themeStore,
  window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false
);

type Member = { id: string; display_name: string; role: "parent" | "child"; family_id: string };

function el(html: string): HTMLElement {
  const t = document.createElement("template");
  t.innerHTML = html.trim();
  return t.content.firstElementChild as HTMLElement;
}

// The top-bar date header (mockup-v9): a bold main label + a muted month/year sub. Today/Day →
// "Thu 13 ·Aug·"; Week → "13–19 ·Aug·"; Month → "Aug ·2026·"; Tasks/Trolley → just the word.
const MON3 = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW3 = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ymd = (s: string): [number, number, number] => s.split("-").map(Number) as [number, number, number];
const dowOf = (s: string): number => { const [y, m, d] = ymd(s); return new Date(Date.UTC(y, m - 1, d)).getUTCDay(); };

// ── auth-gate states ────────────────────────────────────────────────────────
function renderLogin(errorMsg?: string) {
  app.replaceChildren();
  const form = el(`
    <form class="card" novalidate>
      <h1>Household Ops</h1>
      <p class="muted">Sign in to your family space.</p>
      <label>Email<input name="email" type="email" autocomplete="username" required autofocus /></label>
      <label>Password<input name="password" type="password" autocomplete="current-password" required /></label>
      <button type="submit">Sign in</button>
      <p class="error" role="alert">${errorMsg ? escapeHtml(errorMsg) : ""}</p>
    </form>`);
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const btn = form.querySelector("button")!;
    btn.disabled = true;
    btn.textContent = "Signing in…";
    const email = (form.querySelector('[name="email"]') as HTMLInputElement).value.trim();
    const password = (form.querySelector('[name="password"]') as HTMLInputElement).value;
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) renderLogin("That email and password didn't match. Please try again.");
  });
  app.appendChild(form);
}

function renderNotInvited() {
  app.replaceChildren();
  app.appendChild(
    el(`<div class="card"><h1>You're signed in</h1>
      <p class="muted">This account isn't linked to a family space yet. Ask a parent to add you.</p>
      <button id="signout" class="secondary">Sign out</button></div>`)
  );
  document.getElementById("signout")!.addEventListener("click", () => supabase.auth.signOut());
}

// ── the See surface ─────────────────────────────────────────────────────────
async function renderFamily(session: Session) {
  const { data: me } = await supabase
    .from("member")
    .select("id, display_name, role, family_id")
    .eq("auth_user_id", session.user.id)
    .maybeSingle();
  if (!me) return renderNotInvited();
  const member = me as Member;

  const [{ data: fam }, { data: members }, { data: colors }] = await Promise.all([
    supabase.from("family").select("name").maybeSingle(),
    supabase.from("member").select("id, display_name, role"),
    supabase.from("member_color").select("member_id, color"), // family-readable avatar colours (REQ-THEME-008)
  ]);
  const memberList = (members ?? []).map((m: { id: string; display_name: string; role?: string }) => ({ id: m.id, name: m.display_name, role: m.role ?? "parent" }));
  const nameById = new Map(memberList.map((m) => [m.id, m.name]));
  const colorMap = new Map<string, string>((colors ?? []).map((c: { member_id: string; color: string }) => [c.member_id, c.color]));
  // Distinct colour per member: the chosen member_color, else a stable AVATAR_PALETTE colour by the
  // member's position in the family — so two people are never the same colour AND every member's
  // effective colour is a real palette swatch (so the "Make it mine" picker can mark it as taken).
  const ownerColorFallback = new Map(memberList.map((m, i) => [m.id, AVATAR_PALETTE[i % AVATAR_PALETTE.length]]));
  const ownerColor = (id: string | null | undefined) => (id ? colorMap.get(id) ?? ownerColorFallback.get(id) ?? null : null);
  const idByName = new Map(memberList.map((m) => [m.name.toLowerCase(), m.id]));
  const ownerName = (id: string | null | undefined) => (id ? nameById.get(id) ?? "Someone" : "Unassigned");

  // View state (data, not DOM). The active tab persists (REQ-VIEW-009); the All/Mine filter
  // defaults by role — a child starts on Mine (REQ-VIEW-013).
  const TABS: Tab[] = ["today", "calendar", "tasks", "trolley"];
  const storedTab = viewStore.get();
  let activeTab: Tab = TABS.includes(storedTab as Tab) ? (storedTab as Tab) : "today";
  let calMode: CalMode = "month"; // Calendar lands on Month; nav-driven (REQ-VIEW-016)
  let weekLayout: "list" | "board" = "list"; // Week List/Board toggle (REQ-VIEW-018)
  let filter: "all" | "mine" = defaultFilter(member.role);
  let avatarOpen = false;
  const todayLocal = () => todayModel([], new Date().toISOString()).today;
  let cursor: string = todayLocal(); // the local date the window is anchored on (REQ-VIEW-011)
  let trolleyStore = "supermarket";
  // The date-window scope for prev/next + swipe: Today steps a day, the Calendar steps its
  // active mode's window (Day steps a day), and non-dated surfaces don't step.
  const CAL_STEP: Record<CalMode, "today" | "week" | "month"> = { day: "today", week: "week", month: "month" };
  const shiftScope = (): "today" | "week" | "month" | "trolley" =>
    activeTab === "calendar" ? CAL_STEP[calMode] : activeTab === "today" ? "today" : "trolley";
  let theme: "light" | "dark" =
    (document.documentElement.getAttribute("data-theme") as "light" | "dark") || "light";

  // Load this member's personalization and apply it (REQ-THEME-006). AA-safe by construction.
  let prefs: MemberPrefs = {};
  {
    const { data: p } = await supabase
      .from("member_prefs")
      .select("accent, accent_corrected, font, background_path, derived_palette")
      .eq("member_id", member.id)
      .maybeSingle();
    prefs = (p as MemberPrefs) ?? {};
  }
  applyOverrides(document.documentElement, themeOverrides(prefs, theme));
  if (prefs.background_path) {
    const { data: signed } = await supabase.storage.from("backgrounds").createSignedUrl(prefs.background_path, 3600);
    applyBackground(document.body, signed?.signedUrl ?? DEFAULT_BG);
  } else {
    applyBackground(document.body, DEFAULT_BG); // no custom photo → the calm default
  }

  let allItems: ViewItem[] = [];
  // Inbound ingested captures awaiting review (REQ-INGEST-004) + the count diverted to private.
  let inbound: Array<{ captureId: string; extraction: Extraction; from: string; subject: string }> = [];
  let divertedCount = 0;
  let alerts: AlertItem[] = []; // the active "Know" tier heads-ups (REQ-ALERT-002)
  let lastDigest: DigestModel | null = null; // the digest rendered this paint (REQ-DIGEST-001)
  let trap: FocusTrap | null = null; // the capture sheet's focus trap while open

  function ctx(dateLabel: string, dateSub: string, counts: { trolleyDone: number; tasksDone: number; todayCount: number; digest?: DigestModel }): SeeContext {
    return {
      activeTab,
      calMode,
      weekLayout,
      filter,
      me: { id: member.id, name: member.display_name },
      members: memberList,
      ownerName,
      ownerColor,
      role: member.role,
      theme,
      familyName: fam?.name ?? "Household",
      avatarOpen,
      dateLabel,
      dateSub,
      trolleyStore,
      trolleyDone: counts.trolleyDone,
      tasksDone: counts.tasksDone,
      todayCount: counts.todayCount,
      inbound: inbound.length,
      alerts,
      digest: counts.digest,
      todayDate: todayLocal(), // the real today → filled Month cell
      termCtx: termContext(),
    };
  }

  // Term/holiday context markers (006) arrive as source:external:term-context items — used only for
  // the Week-column term label (REQ-VIEW-017/CAL-010), never rendered as events. Derive the spans.
  const TERM_SOURCE = "external:term-context";
  function termContext() {
    const markers = allItems.filter((i) => i.source === TERM_SOURCE && i.due_at);
    if (!markers.length) return null;
    const derived = deriveTerms(
      markers.map((i) => ({
        uid: (i.gcal_event_id as string) ?? i.id,
        summary: i.title,
        start: localDateStr(new Date(i.due_at as string), "Pacific/Auckland"),
        end: null,
        allDay: true,
      }))
    );
    return derived.length ? derived : null;
  }

  // Show all family, or just my own (REQ-VIEW-013). Term-context markers are never visible items.
  function scopedItems(): ViewItem[] {
    return filterByScope(allItems.filter((i) => i.source !== TERM_SOURCE), filter, member.id);
  }

  // paint() only ever touches #app (the shell). The capture sheet lives in #sheet-root, so a
  // theme/toggle/filter repaint — or a token-refresh the guard lets through — never tears down an
  // in-progress capture (REQ-CAPTURE-008).
  function paint() {
    const filtered = scopedItems();
    const iso = cursorISO(cursor);
    const today = todayModel(filtered, iso);
    const week = weekModel(filtered, iso, new Date().toISOString());
    const month = monthModel(filtered, iso);
    const day = dayModel(filtered, iso);
    const tasks = tasksModel(filtered, iso);
    // The top-bar date header — bold main label + muted month/year sub (mockup-v9).
    let dateLabel = "";
    let dateSub = "";
    if (activeTab === "tasks") dateLabel = "Tasks";
    else if (activeTab === "trolley") dateLabel = "Trolley";
    else if (activeTab === "calendar" && calMode === "month") {
      const [y, m] = ymd(cursor);
      dateLabel = MON3[m - 1];
      dateSub = String(y);
    } else if (activeTab === "calendar" && calMode === "week") {
      const a = week.days[0]?.date ?? today.today;
      const b = week.days[6]?.date ?? a;
      dateLabel = `${ymd(a)[2]}–${ymd(b)[2]}`;
      dateSub = MON3[ymd(b)[1] - 1];
    } else {
      // Today / Calendar-Day
      const d = activeTab === "today" ? today.today : cursor;
      dateLabel = `${DOW3[dowOf(d)]} ${ymd(d)[2]}`;
      dateSub = MON3[ymd(d)[1] - 1];
    }
    // The trolley is a shared shopping list — show all family groceries, not the All/Mine scope.
    const trolley = trolleyModel(allItems);
    const trolleyDone = allItems.filter((i) => i.type === "grocery" && i.status === "done").length;
    const tasksDone = allItems.filter((i) => isTodo(i) && i.type !== "grocery" && i.status === "done").length;
    // The live digest for Today (REQ-DIGEST-001) — real "now" (not the viewed cursor) for the period.
    const nowISO = new Date().toISOString();
    lastDigest = digestModel(filtered, nowISO, digestPeriod(nowISO), { inbound: inbound.length });
    app.innerHTML = renderShell(
      { today, week, month, day, tasks, trolley },
      ctx(dateLabel, dateSub, { trolleyDone, tasksDone, todayCount: today.todayItems.length, digest: lastDigest })
    );
    bindSurface();
    wireSwipe();
  }

  async function refresh() {
    // Items + their owners (REQ-ITEM-006). item_owner is the many-to-many; fall back to the legacy
    // owner_member_id for any row not yet mirrored there.
    const [{ data: items }, { data: owners }] = await Promise.all([
      supabase
        .from("item")
        .select("id, title, type, status, due_at, end_at, location, rrule, details, owner_member_id, source, visibility, gcal_event_id, gcal_sync_error")
        .order("due_at", { ascending: true }),
      supabase.from("item_owner").select("item_id, member_id"),
    ]);
    const ownersByItem = new Map<string, string[]>();
    for (const r of (owners ?? []) as Array<{ item_id: string; member_id: string }>) {
      const a = ownersByItem.get(r.item_id) ?? [];
      a.push(r.member_id);
      ownersByItem.set(r.item_id, a);
    }
    allItems = ((items ?? []) as ViewItem[]).map((i) => ({
      ...i,
      owners: ownersByItem.get(i.id) ?? (i.owner_member_id ? [i.owner_member_id] : []),
    }));
    // Inbound ingestion (REQ-INGEST-004): ready-to-review ingested captures (RLS shows only the
    // pinned owner their own), plus a diverted-to-private count (parent-only via health_queue RLS),
    // plus the active "Know" tier heads-ups (REQ-ALERT-002).
    const [{ data: caps }, { count: hqCount }, { data: alertRows }] = await Promise.all([
      supabase.from("capture").select("id, extraction, ingest_meta").eq("source", "ingest-gmail").eq("status", "pending").not("extraction", "is", null),
      supabase.from("health_queue").select("id", { count: "exact", head: true }).eq("source", "gmail"),
      supabase.from("alert").select("id, title, summary, url, created_at, dismissed_at, capture_id").is("dismissed_at", null).order("created_at", { ascending: false }).limit(50),
    ]);
    inbound = ((caps ?? []) as Array<{ id: string; extraction: Extraction; ingest_meta: { from?: string; subject?: string } | null }>)
      .filter((c) => c.extraction && c.extraction.ready)
      .map((c) => ({ captureId: c.id, extraction: c.extraction, from: c.ingest_meta?.from ?? "", subject: c.ingest_meta?.subject ?? "" }));
    divertedCount = hqCount ?? 0;
    alerts = activeAlerts((alertRows ?? []) as AlertRow[]);
    paint();
  }

  // Nudge the server-side calendar reconcile (dormant until the owner sets Google creds), then
  // refresh so a synced event shows its marker.
  async function nudgeCalendar() {
    try {
      await supabase.functions.invoke("calendar-sync", { body: {} });
    } catch {
      /* dormant / offline — the item's sync error surfaces on next view */
    }
    await refresh();
  }

  async function changeStatus(id: string, action: ItemAction) {
    let next: ItemStatus;
    try {
      next = nextStatus("open", action); // only open items are shown
    } catch {
      return;
    }
    // A recurring TASK isn't "done for good" — completing this occurrence rolls it to the next
    // one (REQ-CAL-011). The row stays open with due_at advanced. Events aren't roll-forward here
    // (their occurrences are projected read-only; the master row is edited/cancelled directly).
    if (action === "done") {
      const it = allItems.find((i) => i.id === id);
      if (it && it.rrule && isTodo(it) && it.due_at) {
        const roll = completeRecurringTask(it, new Date().toISOString());
        if (roll.status === "open" && roll.due_at) {
          await supabase.from("item").update({ due_at: roll.due_at }).eq("id", id);
          await refresh();
          announce("Done — back next time ✓");
          return;
        }
      }
    }
    await supabase.from("item").update({ status: next }).eq("id", id);
    await refresh();
    void nudgeCalendar(); // a cancelled event needs its calendar entry removed
  }

  // Flip an item between family-visible and private-to-me (REQ-ITEM-005). RLS does the enforcing.
  async function changeVisibility(id: string, next: string) {
    await supabase.from("item").update({ visibility: next }).eq("id", id);
    await refresh();
  }

  function bindSurface() {
    app.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((b) =>
      b.addEventListener("click", () => {
        activeTab = (b.dataset.tab as Tab) ?? "today";
        viewStore.set(activeTab); // persist the last tab (REQ-VIEW-009)
        cursor = todayLocal(); // each tab starts on today's window
        avatarOpen = false;
        paint();
      })
    );
    // Scope: "Viewing Everyone / Just me" — one toggle (mockup-v9), replacing the All/Mine segment.
    app.querySelector('[data-act="scope-toggle"]')?.addEventListener("click", () => {
      filter = filter === "all" ? "mine" : "all";
      paint();
    });
    app.querySelector('[data-act="prev"]')?.addEventListener("click", () => {
      cursor = shiftWindow(shiftScope(), cursor, -1);
      paint();
    });
    app.querySelector('[data-act="next"]')?.addEventListener("click", () => {
      cursor = shiftWindow(shiftScope(), cursor, 1);
      paint();
    });
    // Nav-driven Calendar (REQ-VIEW-016): the header date opens Month; a day cell opens Day.
    app.querySelector('[data-act="open-month"]')?.addEventListener("click", () => {
      const s = openMonth(cursor);
      activeTab = "calendar";
      calMode = s.mode;
      cursor = s.cursor;
      viewStore.set(activeTab);
      avatarOpen = false;
      paint();
    });
    app.querySelectorAll<HTMLButtonElement>('[data-act="open-day"]').forEach((b) =>
      b.addEventListener("click", () => {
        const s = openDay(b.dataset.date!);
        activeTab = "calendar";
        calMode = s.mode;
        cursor = s.cursor;
        paint();
      })
    );
    app.querySelectorAll<HTMLButtonElement>('[data-act="open-week"]').forEach((b) =>
      b.addEventListener("click", () => {
        const s = openWeek(b.dataset.date!); // the week's Monday, so the agenda runs Mon–Sun
        activeTab = "calendar";
        calMode = s.mode;
        cursor = s.cursor;
        paint();
      })
    );
    app.querySelectorAll<HTMLButtonElement>("[data-store]").forEach((b) =>
      b.addEventListener("click", () => {
        trolleyStore = b.dataset.store ?? "supermarket";
        paint();
      })
    );
    app.querySelectorAll<HTMLButtonElement>("[data-week-layout]").forEach((b) =>
      b.addEventListener("click", () => {
        weekLayout = (b.dataset.weekLayout as "list" | "board") ?? "list";
        paint();
      })
    );
    const addForm = app.querySelector<HTMLFormElement>(".trolley-add");
    addForm?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const inp = addForm.querySelector<HTMLInputElement>('[name="grocery"]')!;
      const name = inp.value.trim();
      if (!name) return;
      const qtySel = (addForm.querySelector<HTMLSelectElement>('[name="qty"]')?.value ?? "1");
      const title = joinGroceryTitle(name, qtySel === "1" ? "" : qtySel); // qty 1 → no "1x" prefix
      inp.value = "";
      const qtyEl = addForm.querySelector<HTMLSelectElement>('[name="qty"]');
      if (qtyEl) qtyEl.value = "1"; // reset qty for the next add
      await supabase.from("item").insert({
        family_id: member.family_id,
        created_by: member.id,
        type: "grocery",
        title,
        owner_member_id: null, // a shared shopping-list item
        status: "open",
        source: "trolley",
        visibility: "family",
      });
      await refresh();
    });
    // The avatar lives in both the top bar (phone) and the rail foot (desktop); bind both.
    app.querySelectorAll('[data-act="avatar"]').forEach((b) =>
      b.addEventListener("click", () => {
        avatarOpen = !avatarOpen;
        paint();
      })
    );
    app.querySelector('[data-act="avatar-close"]')?.addEventListener("click", () => {
      avatarOpen = false;
      paint();
    });
    app.querySelector('[data-act="theme"]')?.addEventListener("click", () => {
      theme = toggleTheme(document.documentElement, themeStore, theme);
      applyOverrides(document.documentElement, themeOverrides(prefs, theme)); // accent correction is theme-specific
      paint(); // keep the avatar menu open so the Night/Day label flips
    });
    app.querySelector('[data-act="signout"]')?.addEventListener("click", () => supabase.auth.signOut());
    app.querySelector('[data-act="settings"]')?.addEventListener("click", () => {
      avatarOpen = false;
      paint();
      openSettings();
    });
    app.querySelector('[data-act="account"]')?.addEventListener("click", () => {
      avatarOpen = false;
      paint();
      openAccount();
    });
    // Add is the FAB (phone) and the rail Add (desktop); bind both.
    app.querySelectorAll('[data-act="capture"]').forEach((b) => b.addEventListener("click", () => openCapture()));
    app
      .querySelectorAll<HTMLButtonElement>('button[data-act="done"], button[data-act="cancel"]')
      .forEach((b) => b.addEventListener("click", () => changeStatus(b.dataset.id!, b.dataset.act as ItemAction)));
    app
      .querySelectorAll<HTMLButtonElement>('button[data-act="visibility"]')
      .forEach((b) => b.addEventListener("click", () => changeVisibility(b.dataset.id!, b.dataset.next!)));
    // Row interaction model (REQ-VIEW-014): tap the row's open control → the item sheet;
    // the confirmed Delete (inside its <details>) removes the row. A tap on the location sub-text
    // (REQ-CAL-013↺) opens Maps AT the place instead of opening the sheet.
    app
      .querySelectorAll<HTMLButtonElement>('[data-act="open"], [data-act="open-grocery"]')
      .forEach((b) =>
        b.addEventListener("click", (e) => {
          const loc = (e.target as HTMLElement)?.closest?.<HTMLElement>("[data-map-loc]");
          if (loc?.dataset.mapLoc) { e.preventDefault(); e.stopPropagation(); window.open(mapsSearchUrl(loc.dataset.mapLoc), "_blank", "noopener"); return; }
          openItemSheet(b.dataset.id!);
        })
      );
    // Keyboard: Enter/Space on a focused location opens Maps too.
    app.querySelectorAll<HTMLElement>("[data-map-loc]").forEach((el) =>
      el.addEventListener("keydown", (e) => {
        const k = (e as KeyboardEvent).key;
        if (k === "Enter" || k === " ") { e.preventDefault(); e.stopPropagation(); window.open(mapsSearchUrl(el.dataset.mapLoc!), "_blank", "noopener"); }
      })
    );
    app
      .querySelectorAll<HTMLButtonElement>('button[data-act="delete"]')
      .forEach((b) => b.addEventListener("click", () => deleteItem(b.dataset.id!)));
    // Slipped line → a sheet listing the overdue items (REQ-VIEW-021): clear each with its tick, or
    // "Dismiss all". (Was: jump to the Tasks tab — which the family found unhelpful.)
    app.querySelector('[data-act="open-overdue"]')?.addEventListener("click", () => openSlippedSheet());
    // Today "This week" link → Calendar in Week mode for the current week.
    app.querySelector('[data-act="open-week-view"]')?.addEventListener("click", () => {
      activeTab = "calendar";
      calMode = "week";
      viewStore.set(activeTab);
      avatarOpen = false;
      paint();
    });
    // Trolley "Undo (N)" → the recently-ticked sheet.
    app.querySelector('[data-act="open-undo"]')?.addEventListener("click", () => openUndoSheet("grocery"));
    app.querySelector('[data-act="open-task-undo"]')?.addEventListener("click", () => openUndoSheet("task"));
    app.querySelector('[data-act="open-inbound"]')?.addEventListener("click", () => openInboundReview());
    app.querySelector('[data-act="open-digest"]')?.addEventListener("click", () => openDigest());
    // Dismiss a "Know" tier heads-up (REQ-ALERT-002): optimistic remove + repaint, then persist.
    app.querySelectorAll<HTMLButtonElement>('[data-act="alert-dismiss"]').forEach((b) =>
      b.addEventListener("click", async () => {
        const id = b.dataset.id!;
        alerts = alerts.filter((a) => a.id !== id);
        paint();
        const { error } = await supabase.from("alert").update({ dismissed_at: new Date().toISOString(), dismissed_by: member.id }).eq("id", id);
        if (error) { announce("Couldn't dismiss — try again."); await refresh(); }
      })
    );
    // Promote a heads-up into a task/event (REQ-ALERT-003): open the editable drawer prefilled from it.
    app.querySelectorAll<HTMLButtonElement>('[data-act="alert-promote"]').forEach((b) =>
      b.addEventListener("click", () => {
        const a = alerts.find((x) => x.id === b.dataset.id);
        if (a) promoteAlert(a);
      })
    );
  }

  // Quick "Add to Trolley" (mockup-v9 qadd) — the FAB/rail Add on the Trolley tab adds a grocery to
  // the top of the list, rather than opening the full capture composer.
  function openTrolleyAdd() {
    sheetRoot.innerHTML = sheetShell("qadd-h", "Add to Trolley", "capture-close",
      `<form class="capture" aria-label="Add to trolley">
        <div class="shbody"><label class="f" for="qadd-i">Item</label><input class="input" id="qadd-i" name="grocery" type="text" placeholder="e.g. eggs" autocomplete="off" /></div>
        <div class="sheetfoot"><button type="submit" class="btn primary">Add</button></div>
      </form>`);
    const sheet = sheetRoot.querySelector<HTMLElement>(".sheet")!;
    trap = createFocusTrap(sheet);
    trap.activate();
    (sheet.querySelector("input") as HTMLInputElement | null)?.focus();
    sheet.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Escape") closeSheet(); });
    sheetRoot.querySelectorAll('[data-act="capture-close"]').forEach((b) => b.addEventListener("click", () => closeSheet()));
    sheet.querySelector("form")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      const v = (sheet.querySelector('[name="grocery"]') as HTMLInputElement).value.trim();
      if (!v) return;
      await supabase.from("item").insert({
        family_id: member.family_id, created_by: member.id, type: "grocery", title: v,
        owner_member_id: null, status: "open", source: "trolley", visibility: "family",
      });
      closeSheet();
      await refresh();
    });
  }

  // Recently-ticked items (mockup-v9 undo sheet) — restore a ticked item. Shared by the Trolley
  // (groceries) and the Tasks surface (tasks/notes) so an accidental tick can always be undone.
  function openUndoSheet(kind: "grocery" | "task") {
    const done = allItems.filter((i) =>
      i.status === "done" && (kind === "grocery" ? i.type === "grocery" : isTodo(i) && i.type !== "grocery")
    );
    const rows = done
      .map((g) => `<li class="item"><span class="it-title" style="color:var(--color-text-muted);text-decoration:line-through">${escapeHtml(g.title)}</span><button type="button" class="row-btn" data-act="undo-item" data-id="${escapeHtml(g.id)}" style="margin-left:auto">Undo</button></li>`)
      .join("") || `<p class="muted" style="padding:12px">Nothing yet.</p>`;
    sheetRoot.innerHTML = sheetShell("undo-h", "Recently ticked off", "capture-close",
      `<div class="shbody"><ul class="panel items" style="margin-bottom:16px">${rows}</ul></div>`);
    const sheet = sheetRoot.querySelector<HTMLElement>(".sheet")!;
    trap = createFocusTrap(sheet);
    trap.activate();
    sheet.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Escape") closeSheet(); });
    sheetRoot.querySelectorAll('[data-act="capture-close"]').forEach((b) => b.addEventListener("click", () => closeSheet()));
    sheet.querySelectorAll<HTMLButtonElement>('[data-act="undo-item"]').forEach((b) =>
      b.addEventListener("click", async () => {
        await supabase.from("item").update({ status: "open" }).eq("id", b.dataset.id!);
        closeSheet();
        await refresh();
      })
    );
  }

  // The Slipped sheet (REQ-VIEW-021) — the overdue items, cleared one at a time or all at once.
  function openSlippedSheet() {
    trap?.release();
    trap = null;
    const overdue = tasksModel(scopedItems(), cursorISO(cursor)).overdue;
    const nowISO = new Date().toISOString();
    const list = overdue.map((it) => ({ id: it.id, title: it.title, sub: it.due_at ? relativeDayLabel(it.due_at, nowISO) : undefined }));
    sheetRoot.innerHTML = slippedSheetHTML(list);
    const sheet = sheetRoot.querySelector<HTMLElement>(".sheet")!;
    trap = createFocusTrap(sheet);
    trap.activate();
    sheet.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Escape") closeSheet(); });
    sheetRoot.querySelectorAll('[data-act="capture-close"]').forEach((b) => b.addEventListener("click", () => closeSheet()));
    sheet.querySelectorAll<HTMLButtonElement>('[data-act="slip-done"]').forEach((b) =>
      b.addEventListener("click", async () => {
        await changeStatus(b.dataset.id!, "done");
        openSlippedSheet(); // re-render with the item cleared
      })
    );
    sheet.querySelectorAll<HTMLButtonElement>('[data-act="slip-open"]').forEach((b) =>
      b.addEventListener("click", () => openItemSheet(b.dataset.id!))
    );
    sheet.querySelector('[data-act="slip-dismiss-all"]')?.addEventListener("click", async () => {
      for (const it of overdue) await supabase.from("item").update({ status: "done" }).eq("id", it.id);
      closeSheet();
      await refresh();
      announce(`Cleared ${overdue.length} slipped item${overdue.length === 1 ? "" : "s"} ✓`);
    });
  }

  // ── opened-item sheet (REQ-VIEW-014) — where a row's actions live ──────────────
  const TZ = "Pacific/Auckland";

  async function deleteItem(id: string) {
    const it = allItems.find((x) => x.id === id);
    await supabase.from("item").update({ status: "cancelled" }).eq("id", id);
    closeSheet();
    await refresh();
    void nudgeCalendar(); // a removed event needs its calendar entry cleared
    announce(it ? `Removed — ${it.title} ✓` : "Removed ✓");
  }

  const pad2 = (n: number) => String(n).padStart(2, "0");
  // Prefill the Edit form from a refined draft (REQ-CAL-014). Sets every editable control so the
  // member sees exactly what will save; the hidden rrule field + recurrence line keep recurrence
  // changes ("make it weekly") persistable even though there's no visible rrule input.
  function applyDraftToForm(sheet: HTMLElement, draft: Draft) {
    const setVal = (name: string, v: string) => {
      const el = sheet.querySelector<HTMLInputElement>(`[name="${name}"]`);
      if (el) el.value = v;
    };
    if (draft.title != null) setVal("title", draft.title);
    if (draft.type) {
      sheet.querySelectorAll<HTMLElement>("[data-type-opt]").forEach((x) => {
        const on = x.dataset.typeOpt === draft.type;
        x.classList.toggle("on", on);
        x.setAttribute("aria-pressed", String(on));
      });
    }
    const ids = new Set((draft.owners ?? []).map((n) => idByName.get(String(n).toLowerCase())).filter(Boolean));
    sheet.querySelectorAll<HTMLElement>("[data-owner-opt]").forEach((b) => {
      const on = ids.has(b.dataset.ownerOpt);
      b.classList.toggle("on", on);
      b.setAttribute("aria-pressed", String(on));
    });
    if (draft.due_at) {
      const d = new Date(draft.due_at);
      setVal("date", localDateStr(d, TZ));
      setVal("time", `${pad2(d.getHours())}:${pad2(d.getMinutes())}`);
    } else {
      setVal("date", "");
      setVal("time", "");
    }
    // Duration select (REQ-UX-007↺) — derive minutes from the refined start→end; a value that isn't a
    // preset simply falls back to "None" (the select ignores an unknown value).
    const durSel = sheet.querySelector<HTMLSelectElement>('[name="duration"]');
    if (durSel) {
      const s = draft.due_at ? new Date(draft.due_at).getTime() : null;
      const e = draft.end_at ? new Date(draft.end_at).getTime() : null;
      durSel.value = s != null && e != null && e > s ? String(Math.round((e - s) / 60000)) : "";
    }
    setVal("location", draft.location ?? "");
    setVal("details", draft.details ?? "");
    setVal("rrule", draft.rrule ?? "");
    const repSel = sheet.querySelector<HTMLSelectElement>('[name="repeat"]');
    if (repSel) repSel.value = presetForRrule(draft.rrule ?? null);
    const recur = sheet.querySelector<HTMLElement>("[data-recur]");
    if (recur) {
      const label = draft.rrule ? recurrenceLabel(draft.rrule) : "";
      recur.textContent = label ? `↻ Repeats ${label}` : "";
      recur.toggleAttribute("hidden", !label);
    }
    // Refine can fill a progressive-disclosure field that's still collapsed — reveal it so the change
    // is visible (the field is already in the DOM; this just un-hides the block + drops its + button).
    for (const [field, val] of [["location", draft.location], ["repeat", draft.rrule], ["details", draft.details]] as const) {
      if (!val) continue;
      const block = sheet.querySelector<HTMLElement>(`[data-field="${field}"]`);
      if (block) block.hidden = false;
      sheet.querySelector<HTMLElement>(`[data-reveal="${field}"]`)?.remove();
    }
    const dd = sheet.querySelector<HTMLElement>("[data-date-display]"); // keep the friendly date label in sync
    const di = sheet.querySelector<HTMLInputElement>("#item-date");
    if (dd) dd.textContent = formatDateShort(di?.value || "");
  }
  // Reconcile an item's owners (REQ-ITEM-006) — replace item_owner rows + keep owner_member_id as
  // the legacy "primary" (owners[0]). RLS lets any family member assign owners on family items.
  async function setOwners(id: string, owners: string[]) {
    await supabase.from("item_owner").delete().eq("item_id", id);
    if (owners.length) {
      await supabase.from("item_owner").insert(owners.map((m) => ({ item_id: id, member_id: m, family_id: member.family_id })));
    }
    await supabase.from("item").update({ owner_member_id: owners[0] ?? null }).eq("id", id);
  }

  function openItemSheet(id: string) {
    id = id.split("#")[0]; // a recurring-event occurrence carries a synthetic id — edit the master row
    const it = allItems.find((x) => x.id === id);
    if (!it) return;
    const localDate = it.due_at ? localDateStr(new Date(it.due_at), TZ) : "";
    const dd = it.due_at ? new Date(it.due_at) : null;
    const timeValue = dd ? `${pad2(dd.getHours())}:${pad2(dd.getMinutes())}` : ""; // browser-local (NZ)
    const ed = it.end_at ? new Date(it.end_at as string) : null;
    const endTimeValue = ed ? `${pad2(ed.getHours())}:${pad2(ed.getMinutes())}` : "";
    const membersWithColor = memberList.map((m) => ({ id: m.id, name: m.name, color: ownerColor(m.id) }));
    const ownerIds = it.owners && it.owners.length ? it.owners : it.owner_member_id ? [it.owner_member_id] : [];
    sheetRoot.innerHTML = itemSheetHTML(it, localDate, { members: membersWithColor, ownerIds, timeValue, endTimeValue });
    const sheet = sheetRoot.querySelector<HTMLElement>(".sheet")!;
    trap = createFocusTrap(sheet);
    trap.activate();
    (sheet.querySelector("input, button") as HTMLElement | null)?.focus();
    sheet.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Escape") closeSheet();
    });
    sheetRoot.querySelectorAll('[data-act="item-close"]').forEach((b) => b.addEventListener("click", () => closeSheet()));
    // Delete is 2-step (mockup-v9 footer link) — one tap arms it, a second confirms. No single-tap
    // destroy (REQ-VIEW-014).
    const delBtn = sheet.querySelector<HTMLButtonElement>('[data-act="delete"]');
    let delArmed = false;
    delBtn?.addEventListener("click", () => {
      if (!delArmed) {
        delArmed = true;
        delBtn.textContent = "Tap again to delete";
        delBtn.classList.add("armed");
        return;
      }
      void deleteItem(id);
    });
    // Type is single-select; owners are multi-select toggles (mockup-v9 Edit sheet).
    const noteHelp = sheet.querySelector<HTMLElement>("[data-note-help]");
    sheet.querySelectorAll<HTMLButtonElement>("[data-type-opt]").forEach((b) =>
      b.addEventListener("click", () => {
        sheet.querySelectorAll("[data-type-opt]").forEach((x) => { x.classList.remove("on"); x.setAttribute("aria-pressed", "false"); });
        b.classList.add("on");
        b.setAttribute("aria-pressed", "true");
        if (noteHelp) noteHelp.hidden = b.dataset.typeOpt !== "note"; // clarify what a Note is, only when picked
      })
    );
    sheet.querySelectorAll<HTMLButtonElement>("[data-owner-opt]").forEach((b) =>
      b.addEventListener("click", () => {
        const on = b.classList.toggle("on");
        b.setAttribute("aria-pressed", String(on));
      })
    );
    // Progressive disclosure (REQ-UX-007): Location/Repeat/Details start as compact "+ Add" buttons
    // when empty; tapping reveals the field (already in the DOM so its value always submits) and
    // focuses it. revealField is reused by AI-refine when it fills a hidden field.
    const revealField = (field: string) => {
      const block = sheet.querySelector<HTMLElement>(`[data-field="${field}"]`);
      if (block) block.hidden = false;
      sheet.querySelector<HTMLElement>(`[data-reveal="${field}"]`)?.remove();
    };
    sheet.querySelectorAll<HTMLButtonElement>("[data-reveal]").forEach((b) =>
      b.addEventListener("click", () => {
        const field = b.dataset.reveal!;
        revealField(field);
        sheet.querySelector<HTMLElement>(`[data-field="${field}"] input, [data-field="${field}"] select, [data-field="${field}"] textarea`)?.focus();
      })
    );
    // Friendly date label (REQ-UX-007↺): keep the "18 Aug '26" overlay in sync with the native picker.
    const dateInput = sheet.querySelector<HTMLInputElement>("#item-date");
    const dateDisplay = sheet.querySelector<HTMLElement>("[data-date-display]");
    const syncDate = () => { if (dateDisplay) dateDisplay.textContent = formatDateShort(dateInput?.value || ""); };
    dateInput?.addEventListener("change", syncDate);
    syncDate();
    // Keyboard remedy (REQ-UX-007↺): on a tall Edit sheet the on-screen keyboard would scroll the
    // focused field (esp. Title, at the top) off the top of the screen. When any text field gains
    // focus, once the keyboard has animated in, scroll it back into the middle of the visible area.
    sheet.addEventListener("focusin", (e) => {
      const el = e.target as HTMLElement;
      if (el.matches?.('input:not([type="date"]), textarea, select')) {
        setTimeout(() => el.scrollIntoView({ block: "center" }), 300);
      }
    });
    // Location actions (REQ-CAL-013) — open Google Maps to search/confirm or navigate. The location
    // text only leaves the app on the member's own tap; nothing is stored externally.
    const locValue = () => (sheet.querySelector('[name="location"]') as HTMLInputElement | null)?.value.trim() ?? "";
    sheet.querySelector('[data-act="find-on-map"]')?.addEventListener("click", () => {
      const loc = locValue();
      if (loc) window.open(mapsSearchUrl(loc), "_blank", "noopener"); // never open the Maps home page on empty
      else announce("Add a location first.");
    });
    sheet.querySelector('[data-act="navigate"]')?.addEventListener("click", () => {
      const url = mapsDirectionsUrl(locValue());
      if (url) window.open(url, "_blank", "noopener");
      else announce("Add a location first for directions.");
    });
    // Repeat picker (REQ-ITEM-008) — a preset → the hidden rrule the Save reads; a task or event can
    // now set/clear a repeat here (not only via AI refine). "custom" keeps a richer AI-set rule.
    const repeatSel = sheet.querySelector<HTMLSelectElement>('[name="repeat"]');
    const rruleHidden = sheet.querySelector<HTMLInputElement>('[name="rrule"]');
    const recurNote = sheet.querySelector<HTMLElement>("[data-recur]");
    repeatSel?.addEventListener("change", () => {
      if (repeatSel.value === "custom") return; // leave the existing custom rrule untouched
      const rr = rruleForPreset(repeatSel.value);
      if (rruleHidden) rruleHidden.value = rr;
      if (recurNote) {
        recurNote.textContent = rr ? `Repeats ${recurrenceLabel(rr)}` : "";
        recurNote.hidden = !rr;
      }
    });
    // Conversational Refine (REQ-CAL-014) — describe a change; the model returns an updated draft
    // that PREFILLS the form for review. Nothing is saved until the member taps Save (Principle 7).
    const refinePanel = sheet.querySelector<HTMLElement>("[data-refine-panel]");
    const refineToggle = sheet.querySelector<HTMLButtonElement>('[data-act="refine-toggle"]');
    const lowerHalf = sheet.querySelector<HTMLElement>("[data-lower]");
    const editFootEl = sheet.querySelector<HTMLElement>("[data-editfoot]");
    // Refine "focus mode" (REQ-UX-007↺): opening collapses the lower half + Save so the input and its
    // Apply button sit just above the keyboard, and Apply is the sole CTA. Restored on close / apply.
    const setRefineMode = (on: boolean) => {
      refinePanel!.toggleAttribute("hidden", !on);
      refineToggle!.setAttribute("aria-expanded", String(on));
      if (lowerHalf) lowerHalf.hidden = on;
      if (editFootEl) editFootEl.hidden = on;
      if (on) (refinePanel!.querySelector('[name="refine"]') as HTMLElement | null)?.focus();
    };
    refineToggle?.addEventListener("click", () => setRefineMode(refinePanel!.hasAttribute("hidden")));
    sheet.querySelector('[data-act="refine-run"]')?.addEventListener("click", async () => {
      const input = sheet.querySelector('[name="refine"]') as HTMLInputElement | null;
      const status = sheet.querySelector("[data-refine-status]") as HTMLElement | null;
      const instruction = input?.value.trim() ?? "";
      if (!instruction) return;
      if (status) { status.classList.remove("err"); status.textContent = "Thinking…"; }
      try {
        const { data, error } = await supabase.functions.invoke("item-refine", { body: { itemId: id, instruction } });
        const draft = (data as { extraction?: { draft?: Draft } } | null)?.extraction?.draft;
        if (error || !draft) throw new Error(error?.message ?? "no result");
        applyDraftToForm(sheet, draft);
        if (input) input.value = "";
        if (status) status.textContent = "";
        setRefineMode(false); // leave focus mode → reveal the updated form + Save for review
        announce("Updated — review and Save.");
      } catch (err) {
        console.error("refine failed:", err);
        if (status) { status.classList.add("err"); status.textContent = "Couldn't refine that — try rephrasing."; }
      }
    });
    sheet.querySelector<HTMLFormElement>("form.item-edit")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      // Grocery: name + quantity round-trip through the title.
      if (it.type === "grocery") {
        const gname = (sheet.querySelector('[name="gname"]') as HTMLInputElement | null)?.value.trim() ?? "";
        const gqty = (sheet.querySelector('[name="gqty"]') as HTMLInputElement | null)?.value ?? "";
        if (gname) await supabase.from("item").update({ title: joinGroceryTitle(gname, gqty) }).eq("id", id);
        closeSheet();
        await refresh();
        announce("Saved ✓");
        return;
      }
      const title = (sheet.querySelector('[name="title"]') as HTMLInputElement | null)?.value.trim();
      const typeSel = (sheet.querySelector('[data-type-opt].on') as HTMLElement | null)?.dataset.typeOpt ?? it.type;
      const dateVal = (sheet.querySelector('[name="date"]') as HTMLInputElement | null)?.value ?? "";
      const timeVal = (sheet.querySelector('[name="time"]') as HTMLInputElement | null)?.value ?? "";
      const durMin = parseInt((sheet.querySelector('[name="duration"]') as HTMLSelectElement | null)?.value ?? "", 10);
      const locVal = (sheet.querySelector('[name="location"]') as HTMLInputElement | null)?.value.trim() ?? "";
      const rruleVal = (sheet.querySelector('[name="rrule"]') as HTMLInputElement | null)?.value.trim() ?? "";
      const detailsVal = (sheet.querySelector('[name="details"]') as HTMLTextAreaElement | null)?.value.trim() ?? "";
      // Combine date + time as browser-local (NZ) → ISO. No date → undated.
      const dueAt = dateVal ? new Date(`${dateVal}T${timeVal || "00:00"}:00`).toISOString() : null;
      // Duration → end_at (REQ-UX-007↺): end = start + N minutes; "None"/no start clears it.
      const endAt = dueAt && timeVal && durMin > 0 ? new Date(new Date(dueAt).getTime() + durMin * 60_000).toISOString() : null;
      const owners = [...sheet.querySelectorAll<HTMLElement>("[data-owner-opt].on")].map((b) => b.dataset.ownerOpt!);
      const wantPriv = (sheet.querySelector('[name="private"]') as HTMLInputElement | null)?.checked ?? false;
      const upd: Record<string, unknown> = { due_at: dueAt, end_at: endAt, location: locVal || null, rrule: rruleVal || null, details: detailsVal || null, type: typeSel };
      if (title) upd.title = title;
      if (wantPriv !== (it.visibility === "private")) upd.visibility = wantPriv ? "private" : "family"; // REQ-ITEM-005
      const { error } = await supabase.from("item").update(upd).eq("id", id);
      if (error) {
        console.error("save item failed:", error);
        return;
      }
      await setOwners(id, owners);
      closeSheet();
      await refresh();
      if (typeSel === "event") void nudgeCalendar();
      announce("Saved ✓");
    });
  }

  // Thin swipe layer over the See area — moves the date window (REQ-VIEW-011). The window logic
  // (shiftWindow) is pure and tested; this just reads the gesture.
  function wireSwipe() {
    const see = app.querySelector<HTMLElement>("#see-main");
    // Swipe steps the date window only on the dated surfaces (Today + Calendar).
    if (!see || (activeTab !== "today" && activeTab !== "calendar")) return;
    let startX = 0;
    see.addEventListener("touchstart", (e) => (startX = e.touches[0].clientX), { passive: true });
    see.addEventListener("touchend", (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 60) {
        cursor = shiftWindow(shiftScope(), cursor, dx < 0 ? 1 : -1);
        paint();
      }
    });
  }

  // ── capture sheet lifecycle (focused dialog in #sheet-root) ────────────────
  function openCapture() {
    if (activeTab === "trolley") return openTrolleyAdd(); // the Trolley Add is a quick grocery add
    sheetRoot.innerHTML = captureSheetHTML();
    const sheet = sheetRoot.querySelector<HTMLElement>(".sheet")!;
    trap = createFocusTrap(sheet);
    trap.activate();
    (sheet.querySelector("textarea") as HTMLTextAreaElement | null)?.focus();
    sheet.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Escape") closeSheet();
    });
    sheetRoot.querySelectorAll('[data-act="capture-close"]').forEach((b) =>
      b.addEventListener("click", () => closeSheet())
    );
    wireComposer();
  }

  // Turn a "Know" heads-up into a real task/event (REQ-ALERT-003): open the editable review drawer
  // prefilled from the heads-up (title + summary, as a task), letting the family pick task-vs-event and
  // a date; on Add the item is created and the heads-up is dismissed (its job is done).
  function promoteAlert(alert: AlertItem) {
    sheetRoot.innerHTML = captureSheetHTML(); // the sheet shell (scrim + .sheet); the form replaces its body
    const sheet = sheetRoot.querySelector<HTMLElement>(".sheet")!;
    trap = createFocusTrap(sheet);
    trap.activate();
    sheet.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Escape") closeSheet(); });
    renderDraftForm(alert.captureId ?? null, alertToExtraction(alert), false, sheet, async () => {
      await supabase.from("alert").update({ dismissed_at: new Date().toISOString(), dismissed_by: member.id }).eq("id", alert.id);
    });
  }

  function closeSheet() {
    trap?.release(); // returns focus to the trigger
    trap = null;
    sheetRoot.innerHTML = "";
  }

  // ── "Make it mine" settings sheet — curated Themes (REQ-THEME-001↺) ──
  function openSettings() {
    trap?.release(); // re-entrant: photo add/remove re-renders this sheet
    const selectedTheme = prefs.accent ? nearestTheme(prefs.accent) : "calm"; // migrate a free 004 accent
    sheetRoot.innerHTML = settingsSheetHTML(selectedTheme, !!prefs.background_path);
    const sheet = sheetRoot.querySelector<HTMLElement>(".sheet")!;
    trap = createFocusTrap(sheet);
    trap.activate();
    wireBackground(sheet);
    renderSwatches(sheet, prefs.derived_palette ?? []); // colours from a previously-set photo
    sheet.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Escape") closeSheet();
    });
    sheetRoot.querySelectorAll('[data-act="settings-close"]').forEach((b) =>
      b.addEventListener("click", () => closeSheet())
    );
    // Theme cards apply live (AA-corrected) and persist to member_prefs.
    sheet.querySelectorAll<HTMLButtonElement>("[data-theme-preset]").forEach((b) =>
      b.addEventListener("click", async () => {
        const upd = buildThemePrefsUpdate(b.dataset.themePreset!);
        const { error } = await supabase.from("member_prefs").upsert({ member_id: member.id, ...upd });
        if (error) {
          console.error("save theme failed:", error);
          return;
        }
        prefs = { ...prefs, accent: upd.accent, accent_corrected: upd.accent_corrected };
        applyOverrides(document.documentElement, themeOverrides(prefs, theme));
        sheet.querySelectorAll<HTMLButtonElement>("[data-theme-preset]").forEach((x) =>
          x.setAttribute("aria-pressed", String(x === b))
        );
        paint();
        announce("Theme updated ✓");
      })
    );
    // Avatar colour palette (REQ-THEME-008) — pick your colour; colours taken by others are marked.
    function renderAvatarPalette() {
      const box = sheet.querySelector<HTMLElement>("[data-avatar-palette]");
      if (!box) return;
      // Use each member's EFFECTIVE colour (chosen, else palette fallback) so others' in-use colours
      // show as taken — and my current colour shows selected — even before anyone has picked one.
      const withColor = memberList.map((m) => ({ id: m.id, name: m.name, color: ownerColor(m.id) }));
      box.innerHTML = avatarPaletteHTML(AVATAR_PALETTE, ownerColor(member.id), takenColours(withColor, member.id));
      box.querySelectorAll<HTMLButtonElement>("[data-avatar-color]").forEach((b) =>
        b.addEventListener("click", async () => {
          const color = b.dataset.avatarColor!;
          const { error } = await supabase
            .from("member_color")
            .upsert({ member_id: member.id, family_id: member.family_id, color });
          if (error) {
            console.error("save colour failed:", error);
            return;
          }
          colorMap.set(member.id, color);
          renderAvatarPalette();
          paint();
          announce("Colour updated ✓");
        })
      );
    }
    renderAvatarPalette();

    // The font picker is retired in 004.1 (the type scale is fixed by the design system); any
    // font a member set under 004 still applies via themeOverrides, but there is no picker.
    sheet.querySelector("form")!.addEventListener("submit", (e) => {
      e.preventDefault();
      closeSheet();
      paint();
    });
  }

  // Background upload / remove (REQ-THEME-003) — applies live, stored per-member in Storage.
  function wireBackground(sheet: HTMLElement) {
    const bgInput = sheet.querySelector<HTMLInputElement>("#bg-input");
    bgInput?.addEventListener("change", async () => {
      const file = bgInput.files?.[0];
      if (!file) return;
      const path = `${member.id}/${crypto.randomUUID()}`;
      const { error } = await supabase.storage.from("backgrounds").upload(path, file, {
        contentType: file.type || "image/jpeg",
      });
      if (error) {
        bgInput.insertAdjacentHTML("afterend", `<p class="error" role="alert">Couldn't upload that photo.</p>`);
        return;
      }
      const previous = prefs.background_path;
      await supabase.from("member_prefs").upsert({ member_id: member.id, background_path: path });
      prefs = { ...prefs, background_path: path };
      if (previous) await supabase.storage.from("backgrounds").remove([previous]).catch(() => {});
      const { data: signed } = await supabase.storage.from("backgrounds").createSignedUrl(path, 3600);
      applyBackground(document.body, signed?.signedUrl ?? null);
      // Derive a palette CLIENT-SIDE from the local file — the image never goes to a cloud model.
      // A photo drives the accent, so auto-pick its first colour and hide the Theme cards; the Theme
      // returns when the photo is removed (they otherwise clash — REQ-THEME-001↺/003↺).
      try {
        const bmp = await createImageBitmap(file);
        const palette = extractPaletteFromImage(bmp, 5);
        await supabase.from("member_prefs").upsert({ member_id: member.id, derived_palette: palette });
        prefs = { ...prefs, derived_palette: palette };
        if (palette.length) await applyAccentFromSwatch(sheet, palette[0]);
      } catch {
        /* palette is a bonus; ignore if the browser can't decode */
      }
      announce("Background updated ✓");
      openSettings(); // re-render: Theme hidden, photo colours + Remove shown
    });
    sheet.querySelector('[data-act="bg-remove"]')?.addEventListener("click", async () => {
      const previous = prefs.background_path;
      await supabase.from("member_prefs").upsert({ member_id: member.id, background_path: null });
      prefs = { ...prefs, background_path: null };
      if (previous) await supabase.storage.from("backgrounds").remove([previous]).catch(() => {});
      applyBackground(document.body, DEFAULT_BG); // revert to the calm default, not blank
      announce("Background removed ✓");
      openSettings(); // re-render: Theme cards return
    });
  }

  // Swatches derived (client-side) from the member's photo → tap to set the accent (AA-corrected).
  function renderSwatches(sheet: HTMLElement, palette: string[]) {
    const box = sheet.querySelector<HTMLElement>("[data-swatches]");
    if (!box) return;
    if (!palette.length) {
      box.innerHTML = "";
      return;
    }
    box.innerHTML =
      `<span class="muted">Colours from your photo</span>` +
      palette
        .map(
          (h) =>
            `<button type="button" class="swatch" data-swatch="${escapeHtml(h)}" style="background:${escapeHtml(h)}" aria-label="Use this colour"></button>`
        )
        .join("");
    box.querySelectorAll<HTMLButtonElement>("[data-swatch]").forEach((b) =>
      b.addEventListener("click", () => applyAccentFromSwatch(sheet, b.dataset.swatch!))
    );
  }

  async function applyAccentFromSwatch(sheet: HTMLElement, hex: string) {
    const upd = buildAccentPrefsUpdate(hex);
    await supabase.from("member_prefs").upsert({ member_id: member.id, ...upd });
    prefs = { ...prefs, accent: upd.accent, accent_corrected: upd.accent_corrected };
    applyOverrides(document.documentElement, themeOverrides(prefs, theme));
    const input = sheet.querySelector<HTMLInputElement>("#accent-input");
    if (input) input.value = hex;
    announce("Colour updated ✓");
  }

  // ── capture composer (opened from ＋ Capture) ──────────────────────────────
  function wireComposer() {
    const sheet = sheetRoot.querySelector<HTMLElement>(".sheet");
    if (!sheet) return;
    const form = sheet.querySelector("form")!;
    const submitBtn = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
    const photoInput = form.querySelector('[name="photo"]') as HTMLInputElement;
    const camMenu = form.querySelector<HTMLElement>("[data-cam-menu]");
    // Camera → a two-way choice so BOTH a live photo and the gallery/screenshot are available
    // (REQ-CAPTURE-011): "Take photo" forces the camera (capture=environment); "Choose photo" opens
    // the library/files. Devices that route a plain picker straight to Photos still get a live camera.
    form.querySelector('[data-act="camera"]')?.addEventListener("click", () => {
      if (camMenu) camMenu.hidden = !camMenu.hidden;
    });
    form.querySelector('[data-act="cam-take"]')?.addEventListener("click", () => {
      photoInput.setAttribute("capture", "environment");
      if (camMenu) camMenu.hidden = true;
      photoInput.click();
    });
    form.querySelector('[data-act="cam-pick"]')?.addEventListener("click", () => {
      photoInput.removeAttribute("capture");
      if (camMenu) camMenu.hidden = true;
      photoInput.click();
    });
    // Voice → speech-to-text into the textarea (REQ-CAPTURE-012). Web Speech API; disabled with a
    // hint where unsupported (e.g. Firefox). Tap to start, tap again to stop; transcript appends.
    wireVoice(form);
    // Picking/taking a photo proceeds STRAIGHT AWAY — no dumping back to the Add screen for a second
    // tap (REQ-CAPTURE-018). requestSubmit() runs the submit handler below (upload → extract → review
    // drawer). Pure decision in onPhotoSelected; requestSubmit fallback-guarded for old browsers.
    photoInput.addEventListener("change", () => {
      const errBox = form.querySelector(".error")!;
      onPhotoSelected(!!photoInput.files?.[0], {
        busy: () => { errBox.textContent = "📷 Reading your photo…"; submitBtn.textContent = "Reading your photo…"; },
        reset: () => { errBox.textContent = ""; submitBtn.textContent = "Add"; },
        submit: () => { if (typeof form.requestSubmit === "function") form.requestSubmit(); },
      });
    });
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const text = (form.querySelector('[name="text"]') as HTMLTextAreaElement).value.trim();
      const file = (form.querySelector('[name="photo"]') as HTMLInputElement).files?.[0];
      // The Health/off-cloud control is removed from the Add UI for now, so capture always takes the
      // Family (cloud) route. The private_health boundary stays in the backend for a later local pipeline.
      const route: Route = "cloud";
      const priv = (form.querySelector('[name="private"]') as HTMLInputElement | null)?.checked ?? false;
      const errBox = form.querySelector(".error")!;
      if (!text && !file) {
        errBox.textContent = "Type something or add a photo.";
        return;
      }
      const btn = submitBtn;
      // The function returns the primary extraction (single-item path) plus `models` — the raw set it
      // proposed. More than one → a review list (REQ-CAPTURE-010); the array is captured per capture.
      let lastModels: ModelExtraction[] = [];
      const invoke = async (captureId: string): Promise<Extraction> => {
        const t0 = performance.now();
        const { data, error } = await supabase.functions.invoke("capture-extract", { body: { captureId } });
        if (error) throw error;
        const d = data as { extraction: Extraction; models?: ModelExtraction[]; timing?: { ocrMs: number; modelMs: number; totalMs: number } };
        lastModels = d.models ?? [];
        // Capture-speed readout (David's ask): server-side ocr/model split + the round-trip as felt by
        // the client. Console-only, numbers only — no capture content. Watch these across a few adds.
        const roundTripMs = Math.round(performance.now() - t0);
        if (d.timing) {
          const engine = (d.extraction as { ocrEngine?: string }).ocrEngine ?? "text";
          console.info(`⏱ capture ${engine}: ocr ${d.timing.ocrMs}ms · model ${d.timing.modelMs}ms · server ${d.timing.totalMs}ms · round-trip ${roundTripMs}ms`);
        } else {
          console.info(`⏱ capture: round-trip ${roundTripMs}ms`);
        }
        return d.extraction;
      };
      btn.disabled = true;
      try {
        if (file) {
          btn.textContent = "Reading your photo…";
          const res = await startPhotoCapture(
            { route, memberId: member.id, familyId: member.family_id, mime: file.type || "image/jpeg" },
            {
              newId: () => crypto.randomUUID(),
              uploadPhoto: async (path) => {
                const { error } = await supabase.storage.from("captures").upload(path, file, { contentType: file.type || "image/jpeg" });
                if (error) throw error;
              },
              insertAttachment: async (row) => {
                const { data, error } = await supabase.from("attachment").insert(row).select("id").single();
                if (error) throw error;
                return data.id as string;
              },
              insertPhotoCapture: async (row) => {
                const { error } = await supabase.from("capture").insert(row);
                if (error) throw error;
              },
              extract: invoke,
            }
          );
          if (res.route === "private_health") {
            errBox.textContent =
              "This photo isn't saved — you'll need to take it again once private capture is available. It wasn't sent to the cloud.";
            form.reset();
          } else if (lastModels.length > 1) {
            renderReview(res.captureId, lastModels, priv);
          } else {
            renderDraft(res.captureId, res.extraction, priv, true); // photo → always review before commit
          }
        } else {
          btn.textContent = "Thinking…";
          const res = await startCapture({ text, route, memberId: member.id, familyId: member.family_id }, {
            newId: () => crypto.randomUUID(),
            insertCapture: async (row) => {
              const { error } = await supabase.from("capture").insert(row);
              if (error) throw error;
            },
            extract: invoke,
          });
          if (res.route === "private_health") {
            errBox.textContent =
              "Private/Health notes go to your local pipeline (arriving later). This wasn't sent to the cloud and wasn't stored.";
            form.reset();
          } else if (lastModels.length > 1) {
            renderReview(res.captureId, lastModels, priv);
          } else {
            renderDraft(res.captureId, res.extraction, priv);
          }
        }
      } catch (err) {
        console.error("capture failed:", err);
        errBox.textContent = file
          ? "Couldn't read that photo — try again, or type the note instead."
          : "Couldn't read that just now — please try again in a moment.";
      } finally {
        btn.disabled = false;
        btn.textContent = "Add";
      }
    });
  }

  // Voice → text (REQ-CAPTURE-012) using the Web Speech API. Appends the transcript to the textarea;
  // tap to start, tap again to stop. Disabled with a hint where the browser has no support.
  function wireVoice(form: HTMLFormElement) {
    const mic = form.querySelector<HTMLButtonElement>('[data-act="mic"]');
    if (!mic) return;
    type SpeechRec = {
      lang: string; interimResults: boolean; continuous: boolean; start(): void; stop(): void;
      onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
      onend: (() => void) | null; onerror: (() => void) | null;
    };
    const w = window as unknown as { SpeechRecognition?: new () => SpeechRec; webkitSpeechRecognition?: new () => SpeechRec };
    const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
    if (!Ctor) {
      mic.disabled = true;
      mic.classList.add("soon");
      mic.title = "Voice input isn't supported on this browser";
      return;
    }
    mic.classList.remove("soon");
    let rec: SpeechRec | null = null;
    let listening = false;
    const stop = () => { listening = false; mic.classList.remove("listening"); mic.setAttribute("aria-pressed", "false"); };
    mic.addEventListener("click", () => {
      if (listening) { rec?.stop(); return; }
      const ta = form.querySelector("textarea") as HTMLTextAreaElement;
      const base = ta.value.trim();
      rec = new Ctor();
      rec.lang = "en-NZ";
      rec.interimResults = true;
      rec.continuous = false;
      rec.onresult = (e) => {
        let s = "";
        for (let i = 0; i < e.results.length; i++) s += e.results[i][0].transcript;
        ta.value = base ? `${base} ${s}` : s;
      };
      rec.onend = stop;
      rec.onerror = stop;
      listening = true;
      mic.classList.add("listening");
      mic.setAttribute("aria-pressed", "true");
      rec.start();
    });
  }

  // Draft handling (Principle 7 — the inference is always shown before it settles). REQ-UX-005: a
  // READY draft confirms compactly (commit + auto-dismissing role=status line + Edit); the full
  // clarify form appears only on a blocking field or explicit Edit.
  // `forceForm` = never auto-commit; always show the review drawer first (REQ-CAPTURE-015). Photos use
  // it: OCR is error-prone, so a photo draft must be checked before anything enters (David: "put junk
  // in my system instead of seeking clarification" — a confidence gate on the photo workflow).
  function renderDraft(captureId: string, extraction: Extraction, priv = false, forceForm = false) {
    const sheet = sheetRoot.querySelector<HTMLElement>(".sheet");
    if (!sheet) return;
    if (!extraction.blockingField && !forceForm) return void commitAndConfirm(captureId, extraction, priv, sheet);
    renderDraftForm(captureId, extraction, priv, sheet);
  }

  // Insert one committed item from a ready draft. Shared by the single-item confirm and the multi-item
  // review commit (REQ-CAPTURE-010). Returns the new item id, or null on a gate/insert failure.
  async function insertDraft(captureId: string | null, ex: Extraction, priv: boolean, source = "capture-chat"): Promise<string | null> {
    const d = ex.draft;
    // Resolve the named owners to member ids. None named → the creator owns it (as before). Named →
    // only the ones that resolve (REQ-CAPTURE-014 multi-owner: child + the appointed adult).
    const ids = d.owners.map((o) => idByName.get(o.toLowerCase())).filter((x): x is string => !!x);
    const ownerRows = d.owners.length === 0 ? [member.id] : ids;
    const { data, error } = await supabase
      .from("item")
      .insert({
        family_id: member.family_id,
        created_by: member.id,
        type: d.type,
        title: d.title,
        owner_member_id: ownerRows[0] ?? null,
        due_at: d.due_at,
        end_at: d.end_at,
        location: d.location,
        rrule: d.rrule,
        details: d.details,
        status: "open",
        source,
        capture_id: captureId,
        visibility: priv ? "private" : "family",
      })
      .select("id")
      .single();
    if (error || !data) return null;
    const newId = data.id as string;
    for (const mid of ownerRows) {
      await supabase.from("item_owner").insert({ item_id: newId, member_id: mid, family_id: member.family_id }).then(() => {}, () => {});
    }
    return newId;
  }

  // Commit a ready draft and show the compact confirmation (REQ-UX-005). Edit opens the just-added
  // item for correction; the confirmation auto-dismisses.
  async function commitAndConfirm(captureId: string | null, ex: Extraction, priv: boolean, sheet: HTMLElement, onCommitted?: () => Promise<void> | void) {
    const d = ex.draft;
    const newId = await insertDraft(captureId, ex, priv);
    if (!newId) return renderDraftForm(captureId, ex, priv, sheet, onCommitted); // fall back to the form
    if (captureId) await supabase.from("capture").update({ status: "committed" }).eq("id", captureId);
    await onCommitted?.(); // e.g. a promoted heads-up dismisses its alert once the item exists (REQ-ALERT-003)
    sheet.innerHTML = confirmationHTML(confirmationModel(d, new Date().toISOString()));
    // Confirm independent of whether that day is on screen (REQ-UX-002); the html is role=status too.
    announce(announcement(d.title, d.due_at ? relativeDayLabel(d.due_at, new Date().toISOString()) : undefined));
    await refresh();
    if (d.type === "event") void nudgeCalendar(); // mirror the new event to the family calendar
    sheet.querySelector('[data-act="capture-edit"]')?.addEventListener("click", () => {
      closeSheet();
      openItemSheet(newId);
    });
    window.setTimeout(() => {
      if (sheetRoot.querySelector(".cap-confirm")) closeSheet();
    }, 2800);
  }

  // The draft's due (an instant, a date-only string, or null) → the date/time input values (local NZ).
  function draftWhenInputs(due: string | null | undefined): { dateValue: string; timeValue: string } {
    if (!due) return { dateValue: "", timeValue: "" };
    if (/^\d{4}-\d{2}-\d{2}$/.test(due)) return { dateValue: due, timeValue: "" }; // date-only (all-day)
    const dt = new Date(due);
    if (Number.isNaN(dt.getTime())) return { dateValue: "", timeValue: "" };
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Pacific/Auckland", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(dt);
    const g = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
    const hh = g("hour") === "24" ? "00" : g("hour");
    return { dateValue: `${g("year")}-${g("month")}-${g("day")}`, timeValue: `${hh}:${g("minute")}` };
  }

  // The review-before-add drawer (REQ-CAPTURE-004/015) — EDITABLE so a wrong OCR read can be corrected
  // before it commits (David: the confirmation must let me fix the values). Shown for a photo always,
  // and for a typed capture with a blocking field or explicit Edit.
  function renderDraftForm(captureId: string | null, extraction: Extraction, priv: boolean, sheet: HTMLElement, onCommitted?: () => Promise<void> | void) {
    const restatement = restatementText(extraction.draft, new Date().toISOString());
    const when = draftWhenInputs(extraction.draft.due_at);
    sheet.innerHTML =
      `<div class="sheet__head"><h2 id="sheet-title">Add this?</h2><button type="button" class="secondary" data-act="capture-close" aria-label="Close">Close</button></div>` +
      draftHTML(extraction, restatement, when);
    const pc = sheet.querySelector('[name="private"]') as HTMLInputElement | null;
    if (pc) pc.checked = priv;
    sheet.querySelector('[data-act="capture-close"]')?.addEventListener("click", () => closeSheet());
    const form = sheet.querySelector("form")!;
    (form.querySelector("#d-title") as HTMLInputElement | null)?.focus();
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const val = (n: string) => (form.querySelector(`[name="${n}"]`) as HTMLInputElement | HTMLSelectElement | null)?.value ?? "";
      const errEl = form.querySelector<HTMLElement>("[data-draft-err]");
      const title = val("d-title").trim();
      const type = (val("d-type") || "task") as ItemType;
      const date = val("d-date");
      const time = val("d-time");
      const curPriv = (form.querySelector('[name="private"]') as HTMLInputElement | null)?.checked ?? priv;
      if (!title) { if (errEl) errEl.textContent = "Give it a title."; (form.querySelector("#d-title") as HTMLInputElement)?.focus(); return; }
      if (type === "event" && !date) { if (errEl) errEl.textContent = "An event needs a date."; (form.querySelector("#d-date") as HTMLInputElement)?.focus(); return; }
      const due_at = date ? normalizeInstant(time ? `${date}T${time}` : date, "Pacific/Auckland") : null;
      const corrected: Extraction = {
        ...extraction,
        draft: { ...extraction.draft, title, type, due_at, location: val("d-loc").trim() || null },
        blockingField: null,
        clarifyingQuestion: null,
        ready: true,
      };
      if (captureId) await supabase.from("capture").update({ extraction: corrected }).eq("id", captureId);
      await commitAndConfirm(captureId, corrected, curPriv, sheet, onCommitted);
    });
    form.querySelector("[data-act='discard']")!.addEventListener("click", async () => {
      if (captureId) await supabase.from("capture").update({ status: "discarded" }).eq("id", captureId);
      closeSheet();
    });
  }

  // Multi-item review (REQ-CAPTURE-010): the model proposed several items → let the family multi-select
  // which to keep (dupes pre-unselected against what's already loaded), then commit the kept, ready set
  // in one go. Not-ready drafts can't be clarified here — they're committed only if the model left them
  // ready; the others are simply skipped (the single-item flow remains the place to clarify one note).
  function renderReview(captureId: string, models: ModelExtraction[], priv: boolean) {
    const existing: ExistingRef[] = allItems.map((i) => ({ title: i.title, due_at: i.due_at, type: i.type ?? "" }));
    const model = multiDraftModel(models, existing);
    const parentNames = memberList.filter((m) => m.role === "parent").map((m) => m.name);
    const paint = () => {
      trap?.release();
      sheetRoot.innerHTML = sheetShell("sheet-title", "Review these", "capture-close", reviewListHTML(model, parentNames));
      const sheet = sheetRoot.querySelector<HTMLElement>(".sheet")!;
      trap = createFocusTrap(sheet);
      trap.activate();
      sheet.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Escape") closeSheet(); });
      sheetRoot.querySelectorAll('[data-act="capture-close"]').forEach((b) => b.addEventListener("click", () => closeSheet()));
      // Conversational clarify (REQ-CAPTURE-014): pick the adult who takes a child → add them as a
      // co-owner alongside the child, drop the prompt, and re-render.
      sheet.querySelectorAll<HTMLButtonElement>("[data-adult-opt]").forEach((b) =>
        b.addEventListener("click", () => {
          const [idxStr, name] = (b.dataset.adultOpt ?? "").split("::");
          const d = model.drafts[Number(idxStr)];
          if (d) {
            if (name && !d.extraction.draft.owners.includes(name)) d.extraction.draft.owners.push(name);
            d.needsAdult = false;
            paint();
          }
        })
      );
      sheet.querySelector<HTMLFormElement>("form.review")!.addEventListener("submit", async (e) => {
        e.preventDefault();
        const keep = new Set(
          Array.from(sheet.querySelectorAll<HTMLInputElement>('input[name="keep"]:checked')).map((el) => Number(el.value))
        );
        const chosen = model.drafts.filter((_, i) => keep.has(i)).map((d) => d.extraction);
        // A draft that clashes with an existing item ENRICHES it (fills a missing owner, refines a
        // date-only time) rather than duplicating; otherwise it's inserted (REQ-CAPTURE-013).
        let added = 0;
        let updated = 0;
        for (const ex of chosen) {
          if (!ex.ready) continue;
          const clash = findClash(ex.draft);
          if (clash) {
            if (await enrichItem(clash, ex.draft)) updated++;
          } else if (await insertDraft(captureId, ex, priv)) {
            added++;
          }
        }
        await supabase.from("capture").update({ status: added + updated ? "committed" : "discarded" }).eq("id", captureId);
        await refresh();
        if (chosen.some((ex) => ex.ready && ex.draft.type === "event")) void nudgeCalendar();
        announce(`Added ${added}${updated ? `, updated ${updated}` : ""}.`);
        closeSheet();
      });
    };
    paint();
  }

  // The inbound "Needs a look" review (REQ-INGEST-004) — ingested email drafts, deduped against
  // what the family already has, each confirm→item / dismiss→discarded. Nothing auto-commits (P7).
  // The Account sheet (feature 011) — mailbox ingestion stats (REQ-STATS-001) + the family feedback
  // board (REQ-FEEDBACK-001/002). Loads its own data on open (both are small, family-scoped reads).
  async function openAccount() {
    trap?.release();
    const nowISO = new Date().toISOString();
    const isParent = member.role !== "child";
    const [{ data: runs }, { data: fbRows }, { data: capRows }, { count: alertCount }, { count: hqCount }, { count: driveCount }] = await Promise.all([
      supabase.from("ingest_run").select("ran_at, listed, filtered, diverted, held, captured, relevant, not_relevant, noticed, failed, dead_lettered").order("ran_at", { ascending: false }).limit(200),
      supabase.from("feedback").select("id, body, status, category, created_at, created_by").order("created_at", { ascending: false }).limit(100),
      supabase.from("capture").select("status").eq("source", "ingest-gmail").limit(2000),
      supabase.from("alert").select("id", { count: "exact", head: true }).is("dismissed_at", null),
      supabase.from("health_queue").select("id", { count: "exact", head: true }).eq("source", "gmail"),
      supabase.from("capture").select("id", { count: "exact", head: true }).eq("source", "ingest-drive"), // Drive live?
    ]);
    const stats = ingestStats((runs ?? []) as IngestRunRow[], nowISO, undefined, (driveCount ?? 0) > 0);
    const mailbox = mailboxState(tallyStatus(capRows as Array<{ status?: string }> | null), alertCount ?? 0, hqCount ?? 0);
    const feedback = feedbackListModel(((fbRows ?? []) as Array<FeedbackRow & { created_by: string }>).map((r) => ({ ...r, author: nameById.get(r.created_by) ?? null })));
    sheetRoot.innerHTML = accountSheetHTML(stats, mailbox, feedback, { nowISO, canSeeDiverted: isParent });
    const sheet = sheetRoot.querySelector<HTMLElement>(".sheet")!;
    trap = createFocusTrap(sheet);
    trap.activate();
    sheet.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Escape") closeSheet(); });
    sheetRoot.querySelectorAll('[data-act="account-close"]').forEach((b) => b.addEventListener("click", () => closeSheet()));
    // "What is this?" segment — pick a category; it drives the hidden field + the textarea placeholder.
    const catInput = sheet.querySelector<HTMLInputElement>('input[name="category"]')!;
    const ta = sheet.querySelector<HTMLTextAreaElement>('textarea[name="idea"]')!;
    sheet.querySelectorAll<HTMLButtonElement>(".fb-seg-btn").forEach((b) =>
      b.addEventListener("click", () => {
        sheet.querySelectorAll<HTMLButtonElement>(".fb-seg-btn").forEach((x) => x.setAttribute("aria-checked", String(x === b)));
        catInput.value = b.dataset.cat ?? "other";
        if (b.dataset.placeholder) ta.setAttribute("placeholder", b.dataset.placeholder);
      })
    );
    sheet.querySelector<HTMLFormElement>("form.fb-form")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      const errEl = sheet.querySelector<HTMLElement>(".fb-err")!;
      const v = validateFeedback(ta.value);
      if (!v.ok) { errEl.textContent = v.error ?? "Try again."; ta.focus(); return; }
      errEl.textContent = "";
      const btn = sheet.querySelector<HTMLButtonElement>('[data-act="fb-send"]')!;
      btn.disabled = true;
      const { error } = await supabase.from("feedback").insert({ family_id: member.family_id, created_by: member.id, body: v.value, category: catInput.value });
      if (error) { errEl.textContent = "Couldn't save — try again."; btn.disabled = false; return; }
      announce("Thanks — idea sent ✓");
      await openAccount(); // re-render with the new idea in the list
    });
  }

  function openInboundReview() {
    if (!inbound.length) return;
    const batch = inbound.slice();
    const existing: ExistingRef[] = allItems.map((i) => ({ title: i.title, due_at: i.due_at, type: i.type ?? "" }));
    const model = multiDraftFromExtractions(batch.map((b) => b.extraction), existing);
    const provenance = batch.map((b) => [b.from, b.subject].filter(Boolean).join(" · "));
    const headNote = divertedCount ? `${divertedCount} diverted to private (handled separately).` : "";
    trap?.release();
    sheetRoot.innerHTML = sheetShell("sheet-title", "Needs a look", "capture-close", reviewListHTML(model, [], { provenance, headNote }));
    const sheet = sheetRoot.querySelector<HTMLElement>(".sheet")!;
    trap = createFocusTrap(sheet);
    trap.activate();
    sheet.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Escape") closeSheet(); });
    sheetRoot.querySelectorAll('[data-act="capture-close"]').forEach((b) => b.addEventListener("click", () => closeSheet()));
    sheet.querySelector<HTMLFormElement>("form.review")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      const keep = new Set(Array.from(sheet.querySelectorAll<HTMLInputElement>('input[name="keep"]:checked')).map((el) => Number(el.value)));
      let added = 0;
      let updated = 0;
      for (let i = 0; i < batch.length; i++) {
        const { captureId, extraction } = batch[i];
        if (keep.has(i) && extraction.ready) {
          const clash = findClash(extraction.draft);
          if (clash) { if (await enrichItem(clash, extraction.draft)) updated++; await supabase.from("capture").update({ status: "committed" }).eq("id", captureId); }
          else if (await insertDraft(captureId, extraction, false, "ingest-gmail")) { added++; await supabase.from("capture").update({ status: "committed" }).eq("id", captureId); }
        } else {
          // dismissed → discarded (draft-before-commit: nothing lingers, nothing auto-commits)
          await supabase.from("capture").update({ status: "discarded" }).eq("id", captureId);
        }
      }
      await refresh();
      if (batch.some((b, i) => keep.has(i) && b.extraction.draft.type === "event")) void nudgeCalendar();
      announce(`Added ${added}${updated ? `, updated ${updated}` : ""}.`);
      closeSheet();
    });
  }

  // The in-app digest sheet (REQ-DIGEST-001) — the live overview for the current period. Tapping a
  // row opens its edit; the inbound line opens the review. Read-only view over the one surface (P6).
  function openDigest() {
    if (!lastDigest) return;
    const c = ctx("", "", { trolleyDone: 0, tasksDone: 0, todayCount: 0, digest: lastDigest });
    trap?.release();
    sheetRoot.innerHTML = digestSheetHTML(lastDigest, c);
    const sheet = sheetRoot.querySelector<HTMLElement>(".sheet")!;
    trap = createFocusTrap(sheet);
    trap.activate();
    sheet.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === "Escape") closeSheet(); });
    sheetRoot.querySelectorAll('[data-act="capture-close"]').forEach((b) => b.addEventListener("click", () => closeSheet()));
    sheet.querySelectorAll<HTMLElement>('[data-act="open"]').forEach((b) => b.addEventListener("click", () => openItemSheet(b.dataset.id!)));
    sheet.querySelector('[data-act="open-inbound"]')?.addEventListener("click", () => openInboundReview());
  }

  // Dedup key (REQ-CAPTURE-013): normalised title + same calendar day + same type. Returns the
  // matching open item so the import can enrich it instead of duplicating.
  function findClash(draft: { title: string; type: string; due_at: string | null }): ViewItem | null {
    const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");
    const day = (iso: string | null) => (iso ? iso.slice(0, 10) : null);
    return (
      allItems.find(
        (i) =>
          i.status !== "cancelled" &&
          (i.type ?? "") === draft.type &&
          norm(i.title) === norm(draft.title) &&
          day(i.due_at) === day(draft.due_at)
      ) ?? null
    );
  }

  // Enrich, don't overwrite: fill a missing owner and refine a date-only time from the import. Never
  // clobbers an existing non-null value with a different one.
  async function enrichItem(existing: ViewItem, draft: { owners: string[]; due_at: string | null; end_at?: string | null; location?: string | null; rrule?: string | null; details?: string | null }): Promise<boolean> {
    const upd: Record<string, unknown> = {};
    const newOwnerIds = draft.owners.map((o) => idByName.get(o.toLowerCase())).filter((x): x is string => !!x);
    const hasOwner = (existing.owners && existing.owners.length) || existing.owner_member_id;
    // Enrich (never clobber): only fill owners if the existing item has none.
    const addOwners = !hasOwner ? newOwnerIds : [];
    if (addOwners.length) upd.owner_member_id = addOwners[0];
    // If the existing time is date-only (local midnight) and the import carries a real time, take it.
    const isDateOnly = existing.due_at ? existing.due_at.endsWith("T00:00:00Z") || /T00:00(:00)?/.test(existing.due_at) : true;
    if (draft.due_at && isDateOnly && draft.due_at !== existing.due_at) upd.due_at = draft.due_at;
    // Fill richer fields only when the existing row lacks them (never clobber a set value, REQ-CAL-011/012).
    if (draft.end_at && !existing.end_at) upd.end_at = draft.end_at;
    if (draft.location && !existing.location) upd.location = draft.location;
    if (draft.rrule && !existing.rrule) upd.rrule = draft.rrule;
    if (draft.details && !existing.details) upd.details = draft.details;
    if (!Object.keys(upd).length && !addOwners.length) return false;
    if (Object.keys(upd).length) await supabase.from("item").update(upd).eq("id", existing.id);
    for (const mid of addOwners) {
      await supabase.from("item_owner").insert({ item_id: existing.id, member_id: mid, family_id: member.family_id }).then(() => {}, () => {});
    }
    return true;
  }

  await refresh();
}

async function route(session: Session | null) {
  sheetRoot.innerHTML = ""; // any open sheet belonged to the previous render
  clearOverrides(document.documentElement); // drop the previous member's personalization
  applyBackground(document.body, null); // and their background
  if (!session) return renderLogin();
  await renderFamily(session);
}

// Only re-render when the signed-in user actually changes. Supabase fires auth events on token
// refresh and tab re-focus too — e.g. returning from the phone's camera — and a blind re-render
// there rebuilds the surface and discards an in-progress capture (regression 2026-08-11).
let currentUserId: string | null | undefined = undefined;
function handleSession(session: Session | null) {
  const uid = session?.user?.id ?? null;
  if (!shouldRerender(currentUserId, uid)) return;
  currentUserId = uid;
  route(session);
}

supabase.auth.getSession().then(({ data }) => handleSession(data.session));
supabase.auth.onAuthStateChange((_event, session) => handleSession(session));
